import React, { createElement, Fragment } from "react";

import {
    AutocompleteState,
    createAutocomplete,
    AutocompleteApi
} from "@algolia/autocomplete-core";
import {
    getAlgoliaResults,
    parseAlgoliaHitHighlight
} from "@algolia/autocomplete-preset-algolia";
import { Hit } from "@algolia/client-search";
import { css } from "@compiled/react";
/** https://github.com/algolia/algoliasearch-client-javascript/issues/1381 */
import algoliasearch from "algoliasearch/dist/algoliasearch-lite.esm.browser";

import SearchIcon from "@atlaskit/icon/glyph/search";

import { Cross } from "@/libraries/wpl/foundations/src/";

// ===================================================================
// Default Algolia css ===============================================
// ===================================================================

// This is a conversion of https://github.com/algolia/autocomplete/blob/next/packages/autocomplete-theme-classic/src/theme.scss
const DefaultSearchBarStyles = css({
    ".aa-Autocomplete *, .aa-DetachedFormContainer *, .aa-Panel *": {
        boxSizing: "border-box"
    },

    ".aa-Autocomplete, .aa-DetachedFormContainer, .aa-Panel": {
        color: "rgba(38, 38, 39, 1)",
        fontFamily: "inherit",
        fontSize: "calc(16 * 1px)",
        fontWeight: "400",
        lineHeight: "1em",
        margin: "0",
        padding: "0",
        textAlign: "left"
    },

    ".aa-Form": {
        alignItems: "center",
        backgroundColor: "rgba(255, 255, 255, 1)",
        border: "1px solid rgba(128, 126, 163, 0.8)",
        borderRadius: "3px",
        display: "flex",
        lineHeight: "1em",
        margin: "0",
        position: "relative",
        width: "100%"
    },

    ".aa-Form:focus-within": {
        borderColor: "rgba(62, 52, 211, 1)",
        boxShadow:
            "rgba(62, 52, 211, 0.2) 0 0 0 2px, inset rgba(62, 52, 211, 0.2) 0 0 0 2px",
        outline: "medium none currentColor"
    },

    ".aa-InputWrapperPrefix": {
        alignItems: "center",
        display: "flex",
        flexShrink: "0",
        height: "44px",
        order: "1"
    },

    ".aa-Label, .aa-LoadingIndicator": {
        cursor: "auto",
        flexShrink: "0",
        height: "100%",
        padding: "0",
        textAlign: "left"
    },

    ".aa-Label svg, .aa-LoadingIndicator svg": {
        color: "rgba(62, 52, 211, 1)",
        height: "auto",
        maxHeight: "20px",
        strokeWidth: "1.6",
        width: "20px"
    },

    ".aa-LoadingIndicator, .aa-SubmitButton": {
        height: "100%",
        paddingInlineStart: "calc(calc(16 * 1 * 1px)*0.75 - 1px)",
        paddingInlineEnd: "calc(calc(16 * 1 * 1px) / 2)",
        width: "calc(calc(16 * 1 * 1px)*1.75 + 20px - 1px)"
    },

    "@media (hover:none) and (pointer:coarse)": {
        ".aa-LoadingIndicator, .aa-SubmitButton": {
            paddingInlineStart: "calc(calc(calc(16 * 1 * 1px) / 2)/2 - 1px)",
            width: "calc(20px + calc(16 * 1 * 1px)*1.25 - 1px)"
        }
    },

    ".aa-SubmitButton": {
        appearance: "none",
        background: "none",
        border: "0",
        margin: "0"
    },

    ".aa-LoadingIndicator": {
        alignItems: "center",
        display: "flex",
        justifyContent: "center"
    },

    ".aa-LoadingIndicator[hidden]": {
        display: "none"
    },

    ".aa-InputWrapper": {
        order: "3",
        position: "relative",
        width: "100%"
    },

    ".aa-Input": {
        appearance: "none",
        background: "none",
        border: "0",
        color: "rgba(38, 38, 39, 1)",
        font: "inherit",
        height: "44px",
        padding: "0",
        width: "100%"
    },

    ".aa-Input::placeholder": {
        color: "rgba(128, 126, 163, 0.6)",
        opacity: "1"
    },

    ".aa-Input:focus": {
        borderColor: "none",
        boxShadow: "none",
        outline: "none"
    },

    ".aa-Input::-webkit-search-cancel-button, .aa-Input::-webkit-search-decoration, .aa-Input::-webkit-search-results-button, .aa-Input::-webkit-search-results-decoration":
        {
            appearance: "none"
        },

    ".aa-InputWrapperSuffix": {
        alignItems: "center",
        display: "flex",
        height: "44px",
        order: "4"
    },

    ".aa-ClearButton": {
        "alignItems": "center",
        "background": "none",
        "border": "0",
        "color": "rgba(128, 126, 163, 0.6)",
        "cursor": "pointer",
        "display": "flex",
        "height": "100%",
        "margin": "0",
        "padding": "0 calc(calc(16 * 1 * 1px)*0.83333 - .5px)",
        "@media (hover:none) and (pointer:coarse)": {
            padding: "0 calc(calc(16 * 1 * 1px)*0.66667 - .5px)"
        }
    },

    ".aa-ClearButton:focus, .aa-ClearButton:hover": {
        color: "rgba(38, 38, 39, 1)"
    },

    ".aa-ClearButton[hidden]": {
        display: "none"
    },

    ".aa-ClearButton svg": {
        strokeWidth: "1.6",
        width: "20px"
    },

    ".aa-Panel": {
        backgroundColor: "rgba(255, 255, 255, 1)",
        borderRadius: "calc(calc(16 * 1 * 1px)/4)",
        boxShadow:
            "0 0 0 1px rgba(35, 38, 59, 0.1), 0 6px 16px -4px rgba(35, 38, 59, 0.15)",
        margin: "8px 0 0",
        overflow: "hidden",
        position: "absolute",
        transition: "opacity .2s ease-in, filter .2s ease-in"
    },

    "@media screen and (prefers-reduced-motion)": {
        ".aa-Panel": {
            transition: "none"
        }
    },

    ".aa-Panel button": {
        appearance: "none",
        background: "none",
        border: "0",
        margin: "0",
        padding: "0"
    },

    ".aa-PanelLayout": {
        height: "100%",
        margin: "0",
        maxHeight: "650px",
        overflowY: "auto",
        padding: "0",
        position: "relative",
        textAlign: "left"
    },

    ".aa-PanelLayoutColumns--twoGolden": {
        display: "grid",
        gridTemplateColumns: "39.2% auto",
        overflow: "hidden",
        padding: "0"
    },

    ".aa-PanelLayoutColumns--two": {
        display: "grid",
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        overflow: "hidden",
        padding: "0"
    },

    ".aa-PanelLayoutColumns--three": {
        display: "grid",
        gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
        overflow: "hidden",
        padding: "0"
    },

    ".aa-Panel--stalled .aa-Source": {
        filter: "grayscale(1)",
        opacity: ".8"
    },

    ".aa-Panel--scrollable": {
        margin: "0",
        maxHeight: "650px",
        overflowX: "hidden",
        overflowY: "auto",
        padding: "calc(calc(16 * 1 * 1px) / 2)",
        scrollbarColor: "rgba(255, 255, 255, 1) rgba(234, 234, 234, 1)",
        scrollbarWidth: "thin"
    },

    ".aa-Panel--scrollable::-webkit-scrollbar": {
        width: "13px"
    },

    ".aa-Panel--scrollable::-webkit-scrollbar-track": {
        backgroundColor: "rgba(234, 234, 234, 1)"
    },

    ".aa-Panel--scrollable::-webkit-scrollbar-thumb": {
        backgroundColor: "rgba(255, 255, 255, 1)",
        borderRadius: "9999px",
        border: "3px solid rgba(234, 234, 234, 1)",
        borderInlineEndWidth: "2px"
    },

    ".aa-Source": {
        margin: "0",
        padding: "0",
        position: "relative",
        width: "100%"
    },

    ".aa-Source:empty": {
        display: "none"
    },

    ".aa-SourceNoResults": {
        fontSize: "1em",
        margin: "0",
        padding: "calc(16 * 1 * 1px)"
    },

    ".aa-List": {
        listStyle: "none",
        margin: "0"
    },

    ".aa-List, .aa-SourceHeader": {
        padding: "0",
        position: "relative"
    },

    ".aa-SourceHeader": {
        margin: "calc(calc(16 * 1 * 1px) / 2) .5em calc(calc(16 * 1 * 1px) / 2) 0"
    },

    ".aa-SourceHeader:empty": {
        display: "none"
    },

    ".aa-SourceHeaderTitle": {
        background: "rgba(255, 255, 255, 1)",
        color: "rgba(62, 52, 211, 1)",
        display: "inline-block",
        fontSize: ".8em",
        fontWeight: "600",
        margin: "0",
        padding: "0 calc(calc(16 * 1 * 1px) / 2) 0 0",
        position: "relative",
        zIndex: "9999"
    },

    ".aa-SourceHeaderLine": {
        borderBlockEnd: "1px solid rgba(62, 52, 211, 1)",
        display: "block",
        height: "2px",
        insetInlineStart: "0",
        margin: "0",
        opacity: ".3",
        padding: "0",
        position: "absolute",
        insetInlineEnd: "0",
        insetBlockStart: "calc(calc(16 * 1 * 1px) / 2)",
        zIndex: "calc(9999 - 1)"
    },

    ".aa-SourceFooterSeeAll": {
        background:
            "linear-gradient(180deg, rgba(255, 255, 255, 1), rgba(128, 126, 163, .14))",
        border: "1px solid rgba(128, 126, 163, 0.6)",
        borderRadius: "5px",
        boxShadow: "inset 0 0 2px #fff, 0 2px 2px -1px rgba(76, 69, 88, .15)",
        color: "inherit",
        fontSize: ".95em",
        fontWeight: "500",
        padding: ".475em 1em .6em",
        textDecoration: "none"
    },

    ".aa-SourceFooterSeeAll:focus, .aa-SourceFooterSeeAll:hover": {
        border: "1px solid rgba(62, 52, 211, 1)",
        color: "rgba(62, 52, 211, 1)"
    },

    ".aa-Item": {
        alignItems: "center",
        borderRadius: "3px",
        cursor: "pointer",
        display: "grid",
        minHeight: "calc(calc(16 * 1 * 1px)*2.5)",
        padding: "calc(calc(calc(16 * 1 * 1px) / 2)/2)"
    },

    ".aa-Item[aria-selected=true]": {
        backgroundColor: "rgba(179, 173, 214, 0.205)"
    },

    ".aa-Item[aria-selected=true] .aa-ActiveOnly, .aa-Item[aria-selected=true] .aa-ItemActionButton":
        {
            visibility: "visible"
        },

    ".aa-ItemIcon": {
        alignItems: "center",
        background: "rgba(255, 255, 255, 1)",
        borderRadius: "3px",
        boxShadow: "inset 0 0 0 1px rgba(128, 126, 163, 0.3)",
        color: "rgba(119, 119, 163, 1)",
        display: "flex",
        flexShrink: "0",
        fontSize: ".7em",
        height: "calc(20px + calc(calc(16 * 1 * 1px) / 2))",
        justifyContent: "center",
        overflow: "hidden",
        strokeWidth: "1.6",
        textAlign: "center",
        width: "calc(20px + calc(calc(16 * 1 * 1px) / 2))"
    },

    ".aa-ItemIcon img": {
        height: "auto",
        maxHeight: "calc(20px + calc(calc(16 * 1 * 1px) / 2) - 8px)",
        maxWidth: "calc(20px + calc(calc(16 * 1 * 1px) / 2) - 8px)",
        width: "auto"
    },

    ".aa-ItemIcon svg": {
        height: "20px",
        width: "20px"
    },

    ".aa-ItemIcon--alignTop": {
        alignSelf: "flex-start"
    },

    ".aa-ItemIcon--noBorder": {
        background: "none",
        boxShadow: "none"
    },

    ".aa-ItemIcon--picture": {
        height: "96px",
        width: "96px"
    },

    ".aa-ItemIcon--picture img": {
        maxHeight: "100%",
        maxWidth: "100%",
        padding: "calc(calc(16 * 1 * 1px) / 2)"
    },

    ".aa-ItemContent": {
        alignItems: "center",
        cursor: "pointer",
        display: "grid",
        gap: "calc(calc(16 * 1 * 1px) / 2)",
        gridGap: "calc(calc(16 * 1 * 1px) / 2)",
        gridAutoFlow: "column",
        lineHeight: "1.25em",
        overflow: "hidden"
    },

    ".aa-ItemContent:empty": {
        display: "none"
    },

    ".aa-ItemContent mark": {
        background: "none",
        color: "rgba(38, 38, 39, 1)",
        fontStyle: "normal",
        fontWeight: "700"
    },

    ".aa-ItemContent--dual": {
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        textAlign: "left"
    },

    ".aa-ItemContent--dual .aa-ItemContentSubtitle, .aa-ItemContent--dual .aa-ItemContentTitle":
        {
            display: "block"
        },

    ".aa-ItemContent--indented": {
        paddingInlineStart: "calc(20px + calc(16 * 1 * 1px))"
    },

    ".aa-ItemContentBody": {
        display: "grid",
        gap: "calc(calc(calc(16 * 1 * 1px) / 2)/2)",
        gridGap: "calc(calc(calc(16 * 1 * 1px) / 2)/2)"
    },

    ".aa-ItemContentTitle": {
        display: "inline-block",
        margin: "0 .5em 0 0",
        maxWidth: "100%",
        overflow: "hidden",
        padding: "0",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
    },

    ".aa-ItemContentSubtitle": {
        fontSize: ".92em"
    },

    ".aa-ItemContentSubtitleIcon:before": {
        borderColor: "rgba(128, 126, 163, .64)",
        borderStyle: "solid",
        content: "",
        display: "inline-block",
        insetInlineStart: "1px",
        position: "relative",
        insetBlockStart: "-3px"
    },

    ".aa-ItemContentSubtitle--inline .aa-ItemContentSubtitleIcon:before": {
        borderWidth: "0 0 1.5px",
        marginInlineStart: "calc(calc(16 * 1 * 1px) / 2)",
        marginInlineEnd: "calc(calc(calc(16 * 1 * 1px) / 2)/2)",
        width: "calc(calc(calc(16 * 1 * 1px) / 2) + 2px)"
    },

    ".aa-ItemContentSubtitle--standalone": {
        alignItems: "center",
        color: "rgba(38, 38, 39, 1)",
        display: "grid",
        gap: "calc(calc(16 * 1 * 1px) / 2)",
        gridGap: "calc(calc(16 * 1 * 1px) / 2)",
        gridAutoFlow: "column",
        justifyContent: "start"
    },

    ".aa-ItemContentSubtitle--standalone .aa-ItemContentSubtitleIcon:before": {
        borderRadius: "0 0 0 3px",
        borderWidth: "0 0 1.5px 1.5px",
        height: "calc(calc(16 * 1 * 1px) / 2)",
        width: "calc(calc(16 * 1 * 1px) / 2)"
    },

    ".aa-ItemContentSubtitleCategory": {
        color: "rgba(128, 126, 163, 1)",
        fontWeight: "500"
    },

    ".aa-ItemContentDescription": {
        color: "rgba(38, 38, 39, 1)",
        fontSize: ".85em",
        maxWidth: "100%",
        overflowX: "hidden",
        textOverflow: "ellipsis"
    },

    ".aa-ItemContentDescription:empty": {
        display: "none"
    },

    ".aa-ItemContentDescription mark": {
        background: "rgba(245, 223, 77, 0.5)",
        color: "rgba(38, 38, 39, 1)",
        fontStyle: "normal",
        fontWeight: "500"
    },

    ".aa-ItemContentDash": {
        color: "rgba(128, 126, 163, 0.6)",
        display: "none",
        opacity: ".4"
    },

    ".aa-ItemContentTag": {
        backgroundColor: "rgba(62, 52, 211, 0.2)",
        borderRadius: "3px",
        margin: "0 .4em 0 0",
        padding: ".08em .3em"
    },

    ".aa-ItemLink, .aa-ItemWrapper": {
        alignItems: "center",
        color: "inherit",
        display: "grid",
        gap: "calc(calc(calc(16 * 1 * 1px) / 2)/2)",
        gridGap: "calc(calc(calc(16 * 1 * 1px) / 2)/2)",
        gridAutoFlow: "column",
        justifyContent: "space-between",
        width: "100%"
    },

    ".aa-ItemLink": {
        color: "inherit",
        textDecoration: "none"
    },

    ".aa-ItemActions": {
        display: "grid",
        gridAutoFlow: "column",
        height: "100%",
        justifySelf: "end",
        margin: "0 calc(calc(16 * 1 * 1px)/-3)",
        padding: "0 2px 0 0"
    },

    ".aa-ItemActionButton": {
        alignItems: "center",
        background: "none",
        border: "0",
        color: "rgba(128, 126, 163, 0.6)",
        cursor: "pointer",
        display: "flex",
        flexShrink: "0",
        padding: "0"
    },

    ".aa-ItemActionButton:focus svg, .aa-ItemActionButton:hover svg": {
        "color": "rgba(38, 38, 39, 1)",
        "@media (hover:none) and (pointer:coarse)": {
            color: "inherit"
        }
    },

    ".aa-ItemActionButton svg": {
        color: "rgba(128, 126, 163, 0.6)",
        margin: "calc(calc(16 * 1 * 1px)/3)",
        strokeWidth: "1.6",
        width: "20px"
    },

    ".aa-ActiveOnly": {
        visibility: "hidden"
    },

    ".aa-PanelHeader": {
        alignItems: "center",
        background: "rgba(62, 52, 211, 1)",
        color: "#fff",
        display: "grid",
        margin: "0",
        padding: "calc(calc(16 * 1 * 1px) / 2) calc(16 * 1 * 1px)",
        position: "relative"
    },

    ".aa-PanelHeader:after": {
        backgroundImage:
            "linear-gradient(rgba(255, 255, 255, 1), rgba(255, 255, 255, 0))",
        insetBlockEnd: "calc(calc(calc(16 * 1 * 1px) / 2)*-1)",
        content: "",
        height: "calc(calc(16 * 1 * 1px) / 2)",
        insetInlineStart: "0",
        pointerEvents: "none",
        position: "absolute",
        insetInlineEnd: "0",
        zIndex: "9999"
    },

    ".aa-PanelFooter": {
        backgroundColor: "rgba(255, 255, 255, 1)",
        boxShadow: "inset 0 1px 0 rgba(128, 126, 163, 0.3)",
        display: "flex",
        justifyContent: "space-between",
        margin: "0",
        padding: "calc(16 * 1 * 1px)",
        position: "relative",
        zIndex: "9999"
    },

    ".aa-PanelFooter:after": {
        backgroundImage:
            "linear-gradient(rgba(255, 255, 255, 0), rgba(128, 126, 163, 0.6))",
        content: "",
        height: "calc(16 * 1 * 1px)",
        insetInlineStart: "0",
        opacity: ".12",
        pointerEvents: "none",
        position: "absolute",
        insetInlineEnd: "0",
        insetBlockStart: "calc(calc(16 * 1 * 1px)*-1)",
        zIndex: "calc(9999 - 1)"
    },

    ".aa-DetachedContainer": {
        background: "rgba(255, 255, 255, 1)",
        insetBlockEnd: "0",
        boxShadow:
            "0 0 0 1px rgba(35, 38, 59, 0.1), 0 6px 16px -4px rgba(35, 38, 59, 0.15)",
        display: "flex",
        flexDirection: "column",
        insetInlineStart: "0",
        margin: "0",
        overflow: "hidden",
        padding: "0",
        position: "fixed",
        insetInlineEnd: "0",
        insetBlockStart: "0",
        zIndex: "9999"
    },

    ".aa-DetachedContainer:after": {
        height: "32px"
    },

    ".aa-DetachedContainer .aa-SourceHeader": {
        margin: "calc(calc(16 * 1 * 1px) / 2) 0 calc(calc(16 * 1 * 1px) / 2) 2px"
    },

    ".aa-DetachedContainer .aa-Panel": {
        backgroundColor: "rgba(255, 255, 255, 1)",
        borderRadius: "0",
        boxShadow: "none",
        flexGrow: "1",
        margin: "0",
        padding: "0",
        position: "relative"
    },

    ".aa-DetachedContainer .aa-PanelLayout": {
        insetBlockEnd: "0",
        boxShadow: "none",
        insetInlineStart: "0",
        margin: "0",
        maxHeight: "none",
        overflowY: "auto",
        position: "absolute",
        insetInlineEnd: "0",
        insetBlockStart: "0",
        width: "100%"
    },

    ".aa-DetachedFormContainer": {
        borderBlockEnd: "1px solid rgba(128, 126, 163, 0.3)",
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        margin: "0",
        padding: "calc(calc(16 * 1 * 1px) / 2)"
    },

    ".aa-DetachedCancelButton": {
        background: "none",
        border: "0",
        borderRadius: "3px",
        color: "rgba(38, 38, 39, 1)",
        cursor: "pointer",
        font: "inherit",
        margin: "0 0 0 calc(calc(16 * 1 * 1px) / 2)",
        padding: "0 calc(calc(16 * 1 * 1px) / 2)"
    },

    ".aa-DetachedCancelButton:focus, .aa-DetachedCancelButton:hover": {
        boxShadow: "inset 0 0 0 1px rgba(128, 126, 163, 0.3)"
    },

    ".aa-DetachedContainer--modal": {
        borderRadius: "6px",
        insetBlockEnd: "inherit",
        height: "auto",
        margin: "0 auto",
        maxWidth: "680px",
        position: "absolute",
        insetBlockStart: "3%"
    },

    ".aa-DetachedContainer--modal .aa-PanelLayout": {
        maxHeight: "500px",
        paddingBlockEnd: "calc(calc(16 * 1 * 1px) / 2)",
        position: "static"
    },

    ".aa-DetachedContainer--modal .aa-PanelLayout:empty": {
        display: "none"
    },

    ".aa-DetachedSearchButton": {
        alignItems: "center",
        backgroundColor: "rgba(255, 255, 255, 1)",
        border: "1px solid rgba(128, 126, 163, 0.8)",
        borderRadius: "3px",
        color: "rgba(128, 126, 163, 0.6)",
        cursor: "pointer",
        display: "flex",
        font: "inherit",
        fontFamily: "inherit",
        fontSize: "calc(16 * 1px)",
        height: "44px",
        margin: "0",
        padding: "0 calc(44px/8)",
        position: "relative",
        textAlign: "left",
        width: "100%"
    },

    ".aa-DetachedSearchButton:focus": {
        borderColor: "rgba(62, 52, 211, 1)",
        boxShadow:
            "rgba(62, 52, 211, 0.2) 0 0 0 3px, inset rgba(62, 52, 211, 0.2) 0 0 0 2px",
        outline: "medium none currentColor"
    },

    ".aa-DetachedSearchButtonIcon": {
        alignItems: "center",
        color: "rgba(62, 52, 211, 1)",
        cursor: "auto",
        display: "flex",
        flexShrink: "0",
        height: "100%",
        justifyContent: "center",
        width: "calc(20px + calc(16 * 1 * 1px))"
    },

    ".aa-DetachedSearchButtonQuery": {
        color: "rgba(38, 38, 39, 1)",
        lineHeight: "1.25em",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
    },

    ".aa-DetachedSearchButtonPlaceholder[hidden]": {
        display: "none"
    },

    ".aa-Detached": {
        height: "100vh",
        overflow: "hidden"
    },

    ".aa-DetachedOverlay": {
        backgroundColor: "rgba(115, 114, 129, 0.4)",
        height: "100vh",
        insetInlineStart: "0",
        margin: "0",
        padding: "0",
        position: "fixed",
        insetInlineEnd: "0",
        insetBlockStart: "0",
        zIndex: "calc(9999 - 1)"
    },

    ".aa-GradientBottom, .aa-GradientTop": {
        height: "calc(calc(16 * 1 * 1px) / 2)",
        insetInlineStart: "0",
        pointerEvents: "none",
        position: "absolute",
        insetInlineEnd: "0",
        zIndex: "9999"
    },

    ".aa-GradientTop": {
        backgroundImage:
            "linear-gradient(rgba(255, 255, 255, 1), rgba(255, 255, 255, 0))",
        insetBlockStart: "0"
    },

    ".aa-GradientBottom": {
        backgroundImage:
            "linear-gradient(rgba(255, 255, 255, 0), rgba(255, 255, 255, 1))",
        borderEndStartRadius: "calc(calc(16 * 1 * 1px)/4)",
        borderEndEndRadius: "calc(calc(16 * 1 * 1px)/4)",
        insetBlockEnd: "0"
    },

    ".aa-DesktopOnly": {
        "@media (hover:none) and (pointer:coarse)": {
            display: "none"
        }
    },

    ".aa-TouchOnly": {
        "@media (hover:hover)": {
            display: "none"
        }
    }
});

// ===================================================================

const ALGOLIA_APP_ID = process.env["ALGOLIA_APP_ID"] as string;
const ALGOLIA_SEARCH_API_KEY = process.env["ALGOLIA_SEARCH_API_KEY"] as string;
const ALGOLIA_SEARCH_INDEX_NAME = "www.atlassian.com.v1";
const ALGOLIA_FACET_KEY = "category";

const algoliaSearchClient = algoliasearch(
    ALGOLIA_APP_ID,
    ALGOLIA_SEARCH_API_KEY
);

type ContentHit = Hit<{
    brand: string;
    categories: string[];
    description: string;
    image: string;
    name: string;
    title: string;
    price: number;
    rating: number;
    type: string;
    url: string;
}>;

type ItemUrlProps = {
    item: ContentHit;
};

type AutoCompleteSearchBarResultProps = {
    isMobileView: boolean;
    panelRef: React.Ref<HTMLDivElement>;
    autocompleteState: AutocompleteState<ContentHit>;
    autocomplete: AutocompleteApi<
        ContentHit,
        // eslint-disable-next-line  @typescript-eslint/no-explicit-any
        React.BaseSyntheticEvent<object, any, any>,
        React.MouseEvent<Element, MouseEvent>,
        React.KeyboardEvent<Element>
    >;
};

const SearchBarStyles = css({
    "position": "relative",
    "bottom": 0,
    "marginInlineStart": "20px",
    "marginInlineEnd": "20px",
    "width": "auto",
    "maxWidth": "100%",
    "zIndex": 5,
    "display": "block",
    "justifyContent": "center",

    ".aa-Form, .aa-Form:focus-within": {
        border: "none",
        borderColor: "transparent",
        outline: "solid transparent",
        boxShadow: "none"
    },

    ".aa-Item": {
        padding: "16px, 8px"
    },

    ".aa-Panel": {
        borderRadius: "15px 15px 15px 15px",
        width: "57rem"
    },

    ".aa-InputWrapper": {
        position: "relative",
        display: "flex",
        verticalAlign: "middle",
        alignItems: "center",
        height: "2.25rem",
        borderWidth: "1px",
        borderStyle: "solid",
        borderRadius: "25px 25px 25px 25px",
        padding: "0 16px",
        borderColor: "#F4F5F7",
        backgroundColor: "#F4F5F7"
    },

    ".close-button": {
        color: "#44546F",
        backgroundColor: "transparent",
        insetInlineEnd: "10px",
        border: "none",
        insetBlockStart: 0,
        insetBlockEnd: 0,
        margin: "auto"
    }
});

const decodeHtml = (html: string): string => {
    const txt = document.createElement("textarea");
    txt.innerHTML = html;
    return txt.value;
};

const SearchTitleResult = ({ hit }: { hit: ContentHit }) => {
    return createElement(
        Fragment,
        {},
        parseAlgoliaHitHighlight({ hit, attribute: "title" }).map(
            ({ value, isHighlighted }, index) => {
                const decodedValue = decodeHtml(value);
                if (isHighlighted) {
                    return createElement("mark", { key: index }, decodedValue);
                }

                return decodedValue;
            }
        )
    );
};

const AutoCompleteSearchBarResult = ({
    isMobileView = false,
    panelRef,
    autocompleteState,
    autocomplete,
    children
}: React.PropsWithChildren<AutoCompleteSearchBarResultProps>) => {
    if (!isMobileView) {
        return (
            <div
                ref={panelRef}
                className={[
                    "aa-Panel",
                    "aa-Panel--desktop",
                    autocompleteState.status === "stalled" &&
                        "aa-Panel--stalled"
                ]
                    .filter(Boolean)
                    .join(" ")}
                {...autocomplete.getPanelProps({})}
            >
                {children}
            </div>
        );
    } else {
        return <>{children}</>;
    }
};

const AutoCompleteSearchBar = ({
    setIsOpenSearch,
    isMobileView = false
}: {
    setIsOpenSearch?: React.Dispatch<React.SetStateAction<boolean>>;
    isMobileView?: boolean;
}) => {
    const [autocompleteState, setAutocompleteState] = React.useState<
        AutocompleteState<ContentHit>
    >({
        collections: [],
        completion: null,
        context: {},
        isOpen: false,
        query: "",
        activeItemId: null,
        status: "idle"
    });

    const autocomplete = React.useMemo(
        () =>
            createAutocomplete<
                ContentHit,
                React.BaseSyntheticEvent,
                React.MouseEvent,
                React.KeyboardEvent
            >({
                // eslint-disable-next-line  @typescript-eslint/no-explicit-any
                onStateChange({ state }: { state: AutocompleteState<any> }) {
                    setAutocompleteState(state);
                },
                insights: true,
                getSources() {
                    return [
                        {
                            sourceId: ALGOLIA_FACET_KEY,
                            getItems({ query }: { query: string }) {
                                return getAlgoliaResults({
                                    searchClient: algoliaSearchClient,
                                    queries: [
                                        {
                                            indexName:
                                                ALGOLIA_SEARCH_INDEX_NAME,
                                            query,
                                            params: {
                                                hitsPerPage: 7
                                            }
                                        }
                                    ]
                                });
                            },
                            getItemUrl({ item }: ItemUrlProps) {
                                return item.url;
                            }
                        }
                    ];
                },
                placeholder: "",
                openOnFocus: false,
                stallThreshold: 500
            }),
        []
    );
    const inputRef = React.useRef<HTMLInputElement>(null);
    const formRef = React.useRef<HTMLFormElement>(null);
    const panelRef = React.useRef<HTMLDivElement>(null);
    const { getEnvironmentProps } = autocomplete;

    React.useEffect(() => {
        if (!formRef.current || !panelRef.current || !inputRef.current) {
            return undefined;
        }

        const { onTouchStart, onTouchMove, onMouseDown } = getEnvironmentProps({
            formElement: formRef.current,
            inputElement: inputRef.current,
            panelElement: panelRef.current
        });

        window.addEventListener("mousedown", onMouseDown);
        window.addEventListener("touchstart", onTouchStart);
        window.addEventListener("touchmove", onTouchMove);

        return () => {
            window.removeEventListener("mousedown", onMouseDown);
            window.removeEventListener("touchstart", onTouchStart);
            window.removeEventListener("touchmove", onTouchMove);
        };
    }, [getEnvironmentProps, autocompleteState.isOpen]);

    return (
        <div
            className="aa-Autocomplete"
            css={[DefaultSearchBarStyles, SearchBarStyles]}
            {...autocomplete.getRootProps({})}
        >
            <form
                ref={formRef}
                className="aa-Form"
                {...autocomplete.getFormProps({
                    inputElement: inputRef.current
                })}
            >
                <div className="aa-InputWrapperPrefix" />
                <div className="aa-InputWrapper">
                    <input
                        className="aa-Input"
                        ref={inputRef}
                        {...autocomplete.getInputProps({
                            inputElement: inputRef.current
                        })}
                    />
                    <label
                        className="aa-Label"
                        {...autocomplete.getLabelProps({})}
                    >
                        <button
                            className="aa-SubmitButton"
                            type="submit"
                            title="Submit"
                        >
                            <SearchIcon label={"search label"} />
                        </button>
                    </label>
                </div>
                <div className="aa-InputWrapperSuffix">
                    {!isMobileView ? (
                        <button
                            className="aa-ClearButton"
                            title="Clear"
                            type="reset"
                            onClick={(e) => {
                                e.preventDefault();
                                if (setIsOpenSearch) {
                                    setIsOpenSearch(false);
                                }
                            }}
                        >
                            <Cross size="24" />
                        </button>
                    ) : null}
                </div>
            </form>

            {autocompleteState.isOpen && (
                <AutoCompleteSearchBarResult
                    isMobileView={isMobileView}
                    panelRef={panelRef}
                    autocompleteState={autocompleteState}
                    autocomplete={autocomplete}
                >
                    <div className="aa-PanelLayout aa-Panel--scrollable">
                        {autocompleteState.collections.map(
                            (collection, index) => {
                                const { source, items } = collection;

                                return (
                                    <section
                                        key={`source-${index}`}
                                        className="aa-Source"
                                    >
                                        {items.length > 0 && (
                                            <ul
                                                className="aa-List"
                                                {...autocomplete.getListProps()}
                                            >
                                                {items.map((item) => {
                                                    return (
                                                        <li
                                                            key={item.objectID}
                                                            className="aa-Item"
                                                            {...autocomplete.getItemProps(
                                                                { item, source }
                                                            )}
                                                        >
                                                            <a
                                                                className="aa-ItemLink"
                                                                href={item.url}
                                                            >
                                                                <div className="aa-ItemWrapper">
                                                                    <div className="aa-ItemContent">
                                                                        <div className="aa-ItemContentBody">
                                                                            <div className="aa-ItemContentTitle">
                                                                                <SearchTitleResult
                                                                                    hit={
                                                                                        item
                                                                                    }
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="aa-ItemActions">
                                                                        <button
                                                                            className="aa-ItemActionButton aa-DesktopOnly aa-ActiveOnly"
                                                                            type="button"
                                                                            title="Select"
                                                                            style={{
                                                                                pointerEvents:
                                                                                    "none"
                                                                            }}
                                                                        >
                                                                            <svg
                                                                                fill="currentColor"
                                                                                viewBox="0 0 24 24"
                                                                            >
                                                                                <path d="M18.984 6.984h2.016v6h-15.188l3.609 3.609-1.406 1.406-6-6 6-6 1.406 1.406-3.609 3.609h13.172v-4.031z" />
                                                                            </svg>
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </a>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        )}
                                    </section>
                                );
                            }
                        )}
                    </div>
                </AutoCompleteSearchBarResult>
            )}
        </div>
    );
};

export default AutoCompleteSearchBar;
